// Axios client with token-refresh interceptor.
//
// Realm routing: a request is "super" if its URL begins with `/super/` or
// `/auth/super/`; otherwise it is "tenant". The request interceptor picks
// the matching token slot (`superAccessToken` vs `accessToken`), and the
// 401 retry refreshes against the matching endpoint and writes the new
// tokens back into the matching slot. On refresh failure we clear only
// the slot for the failing realm so a concurrent session in the other
// realm survives.
//
// We export only the typed `apiClient`. Callers never see the raw axios
// instance.

import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { storage } from '../utils/storage';
import { isTokenExpiredOrExpiring } from '../utils/jwt';
import { endpoints } from './endpoints';
import type { ApiSuccess, RefreshResult } from '../types/api';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

const REFRESH_LOGOUT_EVENT = 'tricivo:auth-logout';

export type LogoutRealm = 'tenant' | 'super';

/**
 * Custom event fired when the refresh flow gives up for a specific realm.
 * Listeners (the Zustand stores) react by clearing in-memory state and
 * routing the user back to the matching login page. The realm tag means
 * a tenant refresh failure no longer anonymises a concurrent super-admin
 * session in the same browser (and vice versa).
 */
export function dispatchLogoutEvent(realm: LogoutRealm = 'tenant'): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(REFRESH_LOGOUT_EVENT, { detail: { realm } }),
    );
  }
}

export function onLogoutEvent(
  realm: LogoutRealm,
  listener: () => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (evt: Event): void => {
    const detail = (evt as CustomEvent<{ realm?: LogoutRealm }>).detail;
    // Default to 'tenant' for back-compat with any older dispatcher that
    // forgot to include the realm — keeps the existing tenant logout
    // behaviour intact even if a stray un-tagged event slips through.
    const eventRealm: LogoutRealm = detail?.realm ?? 'tenant';
    if (eventRealm === realm) listener();
  };
  window.addEventListener(REFRESH_LOGOUT_EVENT, handler);
  return () => window.removeEventListener(REFRESH_LOGOUT_EVENT, handler);
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const instance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30_000,
});

// --- Realm routing -----------------------------------------------------------
//
// A URL is "super" if it points at the super-admin namespace
// (`/super/...`) or the super auth flow (`/auth/super/...`). All other
// URLs are tenant. The URL is the source of truth — we no longer key off
// `storage.get('realm')` for routing because the realm slot was shared
// state between the two realms and races between tabs.

function isSuperUrl(url: string): boolean {
  return url.startsWith('/super/') || url.startsWith('/auth/super/');
}

// --- Request interceptor ----------------------------------------------------

instance.interceptors.request.use((config) => {
  const url = config.url ?? '';
  const token = isSuperUrl(url)
    ? storage.get('superAccessToken')
    : storage.get('accessToken');
  if (token) {
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// --- Response interceptor (token refresh) -----------------------------------

// Independent in-flight guards per realm. A tenant 401 and a super 401
// arriving back-to-back must each get their own refresh attempt — sharing
// a single guard would let the second one resolve with the wrong realm's
// new token.
let tenantRefreshInFlight: Promise<string | null> | null = null;
let superRefreshInFlight: Promise<string | null> | null = null;

async function performRefresh(realm: 'tenant' | 'super'): Promise<string | null> {
  const refreshKey = realm === 'super' ? 'superRefreshToken' : 'refreshToken';
  const accessKey = realm === 'super' ? 'superAccessToken' : 'accessToken';
  const refreshPath =
    realm === 'super' ? endpoints.superAuth.refresh : endpoints.auth.refresh;
  const refreshToken = storage.get(refreshKey);
  if (!refreshToken) return null;
  try {
    const res = await axios.post<ApiSuccess<RefreshResult>>(
      `${BASE_URL}${refreshPath}`,
      { refresh_token: refreshToken },
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 30_000,
      },
    );
    const data = res.data?.data;
    if (!data?.access_token || !data?.refresh_token) return null;
    storage.set(accessKey, data.access_token);
    storage.set(refreshKey, data.refresh_token);
    return data.access_token;
  } catch {
    return null;
  }
}

/**
 * Refresh through the per-realm in-flight guard so concurrent callers (the
 * 401 interceptor and ensureFreshToken) share a single refresh round-trip
 * rather than racing two of them.
 */
function refreshThroughGuard(realm: 'tenant' | 'super'): Promise<string | null> {
  if (realm === 'super') {
    if (!superRefreshInFlight) {
      superRefreshInFlight = performRefresh('super').finally(() => {
        superRefreshInFlight = null;
      });
    }
    return superRefreshInFlight;
  }
  if (!tenantRefreshInFlight) {
    tenantRefreshInFlight = performRefresh('tenant').finally(() => {
      tenantRefreshInFlight = null;
    });
  }
  return tenantRefreshInFlight;
}

/**
 * Ensures the current access token is valid. If it is expired or expiring
 * soon, attempts a proactive refresh (sharing the same in-flight guard as the
 * 401 interceptor). Returns the current (or freshly refreshed) token, or null
 * if no token exists or the refresh fails.
 *
 * Safe to call from non-axios contexts — notably the SSE hooks, which bake the
 * token into the EventSource URL and must re-read a fresh one on every
 * reconnect.
 */
export async function ensureFreshToken(
  realm: 'tenant' | 'super' = 'tenant',
  forceRefresh = false,
): Promise<string | null> {
  const accessKey = realm === 'super' ? 'superAccessToken' : 'accessToken';
  const token = storage.get(accessKey);
  if (!token) return null;

  // `forceRefresh` lets callers refresh a token the client still considers
  // valid — needed because the server can 401 a not-yet-expired token (clock
  // skew, revoked jti, secret rotation). The SSE helper sets it on every
  // reconnect so a server-rejected token can't loop forever unchanged.
  if (forceRefresh || isTokenExpiredOrExpiring(token)) {
    try {
      return await refreshThroughGuard(realm);
    } catch {
      // Refresh failed — session dead; the interceptor's logout flow handles it.
      return null;
    }
  }

  return token;
}

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined;

    // Don't try to refresh on the refresh endpoint itself, the login endpoint,
    // or after we've already retried this request once. Both staff and
    // super-admin auth paths are excluded.
    const url = original?.url ?? '';
    const isRefreshCall =
      url.endsWith(endpoints.auth.refresh) ||
      url.endsWith(endpoints.superAuth.refresh);
    const isLoginCall =
      url.endsWith(endpoints.auth.login) ||
      url.endsWith(endpoints.superAuth.login) ||
      url.endsWith(endpoints.auth.kioskLogin);

    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !isRefreshCall &&
      !isLoginCall
    ) {
      original._retry = true;
      const realm: 'tenant' | 'super' = isSuperUrl(url) ? 'super' : 'tenant';

      try {
        const newToken = await refreshThroughGuard(realm);
        if (newToken) {
          if (!original.headers) {
            original.headers = new AxiosHeaders();
          }
          original.headers.set('Authorization', `Bearer ${newToken}`);
          return instance.request(original);
        }
      } catch {
        // fall through to logout
      }

      // Refresh failed — clear only the failing realm's slots so a
      // concurrent session in the other realm survives, then notify only
      // the matching store.
      if (realm === 'super') {
        storage.clearSuper();
      } else {
        storage.clearTenant();
      }
      dispatchLogoutEvent(realm);
    }

    return Promise.reject(error);
  },
);

export const apiClient = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    instance.get<ApiSuccess<T>>(url, config).then((r) => r.data),
  post: <T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig) =>
    instance.post<ApiSuccess<T>>(url, body, config).then((r) => r.data),
  put: <T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig) =>
    instance.put<ApiSuccess<T>>(url, body, config).then((r) => r.data),
  patch: <T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig) =>
    instance.patch<ApiSuccess<T>>(url, body, config).then((r) => r.data),
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    instance.delete<ApiSuccess<T>>(url, config).then((r) => r.data),

  // Raw call — for endpoints with a custom envelope (e.g. 204 No Content).
  raw: instance,
};

export type ApiClient = typeof apiClient;
